<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="confirmDeleteTeamDialog"
            :data="currentTeam"
            @on-confirm="deleteTeam"
            @on-abort="closeConfirmDeleteTeamDialog"
        >
            <template #title>
                Usuwanie ekipy
            </template>
            <template #content>
                Czy na pewno chcesz usunąć ekipę <strong>{{ currentTeam.name }}</strong>?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <page-title>
            {{ pageTitle }}
        </page-title>

        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    :disabled="actionLoading"
                    @click="onClose"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Dane zleceniobiorcy
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-container>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="outdoorsContractor.name"
                                                label="Nazwa"
                                                outlined
                                                dense
                                                :error-messages="nameErrors"
                                                required
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="6" md="7">
                                            <v-text-field
                                                v-model="outdoorsContractor.contactMail"
                                                label="E-mail"
                                                outlined
                                                :error-messages="contactMailErrors"
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="6" md="5">
                                            <v-text-field
                                                v-model="outdoorsContractor.phoneNumber"
                                                label="Nr telefonu"
                                                outlined
                                                :error-messages="phoneNumberErrors"
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="4" md="6">
                                            <v-text-field
                                                v-model="outdoorsContractor.street"
                                                label="Ulica"
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="4" md="2">
                                            <v-text-field
                                                v-model="outdoorsContractor.postCode"
                                                label="Kod pocztowy"
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="4" md="4">
                                            <v-text-field
                                                v-model="outdoorsContractor.city"
                                                label="Miasto"
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Ekipy
                            </h2>
                        </v-col>
                        <v-col cols="auto">
                            <v-btn
                                color="secondary"
                                fab
                                small
                                class="elevation-1"
                                data-sentry-label="Dodaj ekipę"
                                :disabled="actionLoading"
                                @click="createTeam"
                            >
                                <v-icon>
                                    mdi-plus
                                </v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text v-if="hasTeams">
                                    <v-data-table
                                        :headers="headers"
                                        :items="teams"
                                        disable-pagination
                                        hide-default-footer
                                        item-key="index"
                                        :mobile-breakpoint="0"
                                    >
                                        <template #item.name="{item}">
                                            {{ item.name }}
                                        </template>
                                        <template #item.contactMail="{item}">
                                            {{ parseEmptyValue(item.contactMail) }}
                                        </template>
                                        <template #item.street="{item}">
                                            {{ parseEmptyValue(item.street) }}
                                        </template>
                                        <template #item.postCode="{item}">
                                            {{ parseEmptyValue(item.postCode) }}
                                        </template>
                                        <template #item.city="{item}">
                                            {{ parseEmptyValue(item.city) }}
                                        </template>
                                        <template #item.actions="{item}">
                                            <div class="d-inline-flex">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Otwórz"
                                                            v-on="on"
                                                            @click="openTeam(item.id)"
                                                        >
                                                            <v-icon color="teal">
                                                                mdi-pencil
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Otwórz</span>
                                                </v-tooltip>
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Usuń"
                                                            v-on="on"
                                                            @click="showConfirmDeleteTeamDialog(item)"
                                                        >
                                                            <v-icon color="pink">
                                                                mdi-delete
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Usuń</span>
                                                </v-tooltip>
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-card-text>

                                <v-card-text v-else>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Brak ekip dla zleceniobiorcy.
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                color="success"
                                medium
                                data-sentry-label="Zapisz"
                                :loading="actionLoading"
                                @click="saveOutdoorsContractor"
                            >
                                Zapisz
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import { errorHandler } from '@/utils/errorHandler'
import { required, email, numeric } from 'vuelidate/lib/validators'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import PageTitle from '@/components/common/PageTitle'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'

export default {
    name: 'OutdoorsContractor',
    components: {
        PageTitle,
        ConfirmDialog

    },
    mixins: [ tooltipDelay, parse ],
    beforeRouteEnter(to, from, next) {
        if (typeof to.params.id !== 'undefined') {
            store.dispatch('outdoors/outdoorsSettings/getContractor', to.params.id)
                .then(response => {
                    next(vm => {
                        vm.contractorAction = 'updateContractor'
                        response.data.name ? vm.pageTitle = `Zleceniobiorca - ${response.data.name}` : vm.pageTitle = 'Zleceniobiorca'
                        vm.outdoorsContractor = Object.assign({}, response.data)
                        vm.teams = response.data.outdoorLocalizationTeams
                    })

                })
                .catch(error => {
                    next(error)
                    errorHandler(error)
                })
        } else {
            next(vm => {
                vm.contractorAction = 'createContractor'
                vm.pageTitle = 'Nowy zleceniobiorca'
            })
        }
    },
    data() {
        return {
            pageTitle: null,
            outdoorsContractor: {
                name: null,
                city: null,
                street: null,
                postCode: null,
                contactMail: null,
                phoneNumber: null,
                outdoorLocalizationTeamIds: []
            },
            teams : [],
            currentTeam: {},
            confirmDeleteTeamDialog: false,
            contractorAction: null,
            actionLoading: false,
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    sortable: false
                },
                {
                    text: 'Email',
                    value: 'contactMail',
                    sortable: false
                },
                {
                    text: 'Adres',
                    value: 'street',
                    sortable: false,
                },
                {
                    text: 'Kod pocztowy',
                    value: 'postCode',
                    sortable: false
                },
                {
                    text: 'Miasto',
                    value: 'city',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
            ]
        }
    },
    validations: {
        outdoorsContractor: {
            name: {
                required
            },
            contactMail: {
                email
            },
            phoneNumber: {
                numeric
            }
        }
    },
    computed: {
        hasTeams() {
            return this.teams.length > 0
        },
        nameErrors() {
            const errors = []

            if (!this.$v.outdoorsContractor.name.$dirty) return errors
            if (!this.$v.outdoorsContractor.name.required)
                errors.push('Nazwa jest wymagana.')

            return errors
        },
        contactMailErrors() {
            const errors = []

            if (!this.$v.outdoorsContractor.contactMail.$dirty) return errors
            if (!this.$v.outdoorsContractor.contactMail.email)
                errors.push('Proszę podać poprawny format dla adresu email.')

            return errors
        },
        phoneNumberErrors() {
            const errors = []

            if (!this.$v.outdoorsContractor.phoneNumber.$dirty) return errors
            if (!this.$v.outdoorsContractor.phoneNumber.numeric)
                errors.push('Proszę podać poprawny format dla numeru telefonu.')

            return errors
        }
    },
    methods: {
        createTeam() {
            this.$router.push({
                name: 'outdoorsSettingsOutdoorsTeamsOutdoorsTeamCreate',
                params: {
                    isFromOutdoorsTeams: false
                }
            })
                .catch(error => errorHandler(error))
        },
        openTeam(teamId) {
            this.$router.push({
                name: 'outdoorsSettingsOutdoorsTeamsOutdoorsTeamEdit',
                params: {
                    id: teamId,
                    isFromOutdoorsTeams: false
                }
            })
                .catch(error => errorHandler(error))
        },
        showConfirmDeleteTeamDialog(team) {
            this.currentTeam = team
            this.confirmDeleteTeamDialog = true
        },
        closeConfirmDeleteTeamDialog() {
            this.confirmDeleteTeamDialog = false

            setTimeout(() => {
                this.currentTeam = {}
            }, 500)
        },
        deleteTeam(currentTeam) {
            this.actionLoading = true
            this.$store.dispatch('outdoors/outdoorsSettings/deleteTeam', currentTeam.id)
                .then(() => {
                    const index = this.teams.findIndex(item => item.id === this.currentTeam.id)

                    this.teams.splice(index, 1)
                    const teamIdIndex = this.outdoorsContractor.outdoorLocalizationTeamIds.indexOf(currentTeam.id)

                    this.outdoorsContractor.outdoorLocalizationTeamIds.splice(teamIdIndex, 1)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                    this.closeConfirmDeleteTeamDialog()
                })
        },
        saveOutdoorsContractor() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.actionLoading = true

            this.$store.dispatch(`outdoors/outdoorsSettings/${this.contractorAction}`, this.outdoorsContractor)
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.onClose()
                })
        },
        onClose() {
            this.actionLoading = false
            setTimeout(() => {
                this.$v.$reset()
                this.outdoorsContractor = {}
                this.outdoorLocalizationTeams = []
            }, 500)
            this.$router.push({ name: 'outdoorsSettingsOutdoorsContractors' })
                .catch(error => {
                    errorHandler(error)
                })
        },
    }
}
</script>